/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxPivotGrid from 'devextreme/ui/pivot_grid';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxoDataSourceModule } from './nested/data-source';
import { DxiFieldModule } from './nested/field-dxi';
import { DxoFormatModule } from './nested/format';
import { DxoHeaderFilterModule } from './nested/header-filter';
import { DxoStoreModule } from './nested/store';
import { DxoExportModule } from './nested/export';
import { DxoFieldChooserModule } from './nested/field-chooser';
import { DxoTextsModule } from './nested/texts';
import { DxoFieldPanelModule } from './nested/field-panel';
import { DxoLoadPanelModule } from './nested/load-panel';
import { DxoScrollingModule } from './nested/scrolling';
import { DxoStateStoringModule } from './nested/state-storing';





/**
 * The PivotGrid is a widget that allows you to display and analyze multi-dimensional data from a local storage or an OLAP cube.
 */

export class DxPivotGridComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxPivotGrid;


    /**
     * Allows an end-user to expand/collapse all header items within a header level.
     */
    
    get allowExpandAll(): boolean {
        return this._getOption('allowExpandAll');
    }
    set allowExpandAll(value: boolean) {
        this._setOption('allowExpandAll', value);
    }


    /**
     * Allows a user to filter fields by selecting or deselecting values in the popup menu.
     */
    
    get allowFiltering(): boolean {
        return this._getOption('allowFiltering');
    }
    set allowFiltering(value: boolean) {
        this._setOption('allowFiltering', value);
    }


    /**
     * Allows an end-user to change sorting options.
     */
    
    get allowSorting(): boolean {
        return this._getOption('allowSorting');
    }
    set allowSorting(value: boolean) {
        this._setOption('allowSorting', value);
    }


    /**
     * Allows an end-user to sort columns by summary values.
     */
    
    get allowSortingBySummary(): boolean {
        return this._getOption('allowSortingBySummary');
    }
    set allowSortingBySummary(value: boolean) {
        this._setOption('allowSortingBySummary', value);
    }


    /**
     * Specifies the area to which data field headers must belong.
     */
    
    get dataFieldArea(): string {
        return this._getOption('dataFieldArea');
    }
    set dataFieldArea(value: string) {
        this._setOption('dataFieldArea', value);
    }


    /**
     * Specifies a data source for the pivot grid.
     */
    
    get dataSource(): DevExpress.data.PivotGridDataSource|
        DevExpress.data.PivotGridDataSourceOptions|
        Array<any> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.PivotGridDataSource|
        DevExpress.data.PivotGridDataSourceOptions|
        Array<any>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Configures client-side exporting.
     */
    
    get export(): any {
        return this._getOption('export');
    }
    set export(value: any) {
        this._setOption('export', value);
    }


    /**
     * The Field Chooser configuration options.
     */
    
    get fieldChooser(): any {
        return this._getOption('fieldChooser');
    }
    set fieldChooser(value: any) {
        this._setOption('fieldChooser', value);
    }


    /**
     * Configures the field panel.
     */
    
    get fieldPanel(): any {
        return this._getOption('fieldPanel');
    }
    set fieldPanel(value: any) {
        this._setOption('fieldPanel', value);
    }


    /**
     * Configures the header filter feature.
     */
    
    get headerFilter(): any {
        return this._getOption('headerFilter');
    }
    set headerFilter(value: any) {
        this._setOption('headerFilter', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies whether or not to hide rows and columns with no data.
     */
    
    get hideEmptySummaryCells(): boolean {
        return this._getOption('hideEmptySummaryCells');
    }
    set hideEmptySummaryCells(value: boolean) {
        this._setOption('hideEmptySummaryCells', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies options configuring the load panel.
     */
    
    get loadPanel(): any {
        return this._getOption('loadPanel');
    }
    set loadPanel(value: any) {
        this._setOption('loadPanel', value);
    }


    /**
     * Specifies the layout of items in the row header.
     */
    
    get rowHeaderLayout(): string {
        return this._getOption('rowHeaderLayout');
    }
    set rowHeaderLayout(value: string) {
        this._setOption('rowHeaderLayout', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * A configuration object specifying scrolling options.
     */
    
    get scrolling(): any {
        return this._getOption('scrolling');
    }
    set scrolling(value: any) {
        this._setOption('scrolling', value);
    }


    /**
     * Specifies whether the outer borders of the grid are visible or not.
     */
    
    get showBorders(): boolean {
        return this._getOption('showBorders');
    }
    set showBorders(value: boolean) {
        this._setOption('showBorders', value);
    }


    /**
     * Specifies whether to display the Grand Total column.
     */
    
    get showColumnGrandTotals(): boolean {
        return this._getOption('showColumnGrandTotals');
    }
    set showColumnGrandTotals(value: boolean) {
        this._setOption('showColumnGrandTotals', value);
    }


    /**
     * Specifies whether to display the Total columns.
     */
    
    get showColumnTotals(): boolean {
        return this._getOption('showColumnTotals');
    }
    set showColumnTotals(value: boolean) {
        this._setOption('showColumnTotals', value);
    }


    /**
     * Specifies whether to display the Grand Total row.
     */
    
    get showRowGrandTotals(): boolean {
        return this._getOption('showRowGrandTotals');
    }
    set showRowGrandTotals(value: boolean) {
        this._setOption('showRowGrandTotals', value);
    }


    /**
     * Specifies whether to display the Total rows. Applies only if rowHeaderLayout is "standard".
     */
    
    get showRowTotals(): boolean {
        return this._getOption('showRowTotals');
    }
    set showRowTotals(value: boolean) {
        this._setOption('showRowTotals', value);
    }


    /**
     * Specifies where to show the total rows or columns. Applies only if rowHeaderLayout is "standard".
     */
    
    get showTotalsPrior(): string {
        return this._getOption('showTotalsPrior');
    }
    set showTotalsPrior(value: string) {
        this._setOption('showTotalsPrior', value);
    }


    /**
     * A configuration object specifying options related to state storing.
     */
    
    get stateStoring(): any {
        return this._getOption('stateStoring');
    }
    set stateStoring(value: any) {
        this._setOption('stateStoring', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Strings that can be changed or localized in the PivotGrid widget.
     */
    
    get texts(): any {
        return this._getOption('texts');
    }
    set texts(value: any) {
        this._setOption('texts', value);
    }


    
    get useNativeScrolling(): boolean|
        string {
        return this._getOption('useNativeScrolling');
    }
    set useNativeScrolling(value: boolean|
        string) {
        this._setOption('useNativeScrolling', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }


    /**
     * Specifies whether long text in header items should be wrapped.
     */
    
    get wordWrapEnabled(): boolean {
        return this._getOption('wordWrapEnabled');
    }
    set wordWrapEnabled(value: boolean) {
        this._setOption('wordWrapEnabled', value);
    }

    /**
     * A handler for the cellClick event.
     */
     onCellClick: EventEmitter<any>;

    /**
     * A handler for the cellPrepared event.
     */
     onCellPrepared: EventEmitter<any>;

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the contextMenuPreparing event.
     */
     onContextMenuPreparing: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the exported event.
     */
     onExported: EventEmitter<any>;

    /**
     * A handler for the exporting event.
     */
     onExporting: EventEmitter<any>;

    /**
     * A handler for the fileSaving event.
     */
     onFileSaving: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the allowExpandAllChange event.
     */
     allowExpandAllChange: EventEmitter<boolean>;

    /**
     * A handler for the allowFilteringChange event.
     */
     allowFilteringChange: EventEmitter<boolean>;

    /**
     * A handler for the allowSortingChange event.
     */
     allowSortingChange: EventEmitter<boolean>;

    /**
     * A handler for the allowSortingBySummaryChange event.
     */
     allowSortingBySummaryChange: EventEmitter<boolean>;

    /**
     * A handler for the dataFieldAreaChange event.
     */
     dataFieldAreaChange: EventEmitter<string>;

    /**
     * A handler for the dataSourceChange event.
     */
     dataSourceChange: EventEmitter<DevExpress.data.PivotGridDataSource|
        DevExpress.data.PivotGridDataSourceOptions|
        Array<any>>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the exportChange event.
     */
     exportChange: EventEmitter<any>;

    /**
     * A handler for the fieldChooserChange event.
     */
     fieldChooserChange: EventEmitter<any>;

    /**
     * A handler for the fieldPanelChange event.
     */
     fieldPanelChange: EventEmitter<any>;

    /**
     * A handler for the headerFilterChange event.
     */
     headerFilterChange: EventEmitter<any>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hideEmptySummaryCellsChange event.
     */
     hideEmptySummaryCellsChange: EventEmitter<boolean>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the loadPanelChange event.
     */
     loadPanelChange: EventEmitter<any>;

    /**
     * A handler for the rowHeaderLayoutChange event.
     */
     rowHeaderLayoutChange: EventEmitter<string>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the scrollingChange event.
     */
     scrollingChange: EventEmitter<any>;

    /**
     * A handler for the showBordersChange event.
     */
     showBordersChange: EventEmitter<boolean>;

    /**
     * A handler for the showColumnGrandTotalsChange event.
     */
     showColumnGrandTotalsChange: EventEmitter<boolean>;

    /**
     * A handler for the showColumnTotalsChange event.
     */
     showColumnTotalsChange: EventEmitter<boolean>;

    /**
     * A handler for the showRowGrandTotalsChange event.
     */
     showRowGrandTotalsChange: EventEmitter<boolean>;

    /**
     * A handler for the showRowTotalsChange event.
     */
     showRowTotalsChange: EventEmitter<boolean>;

    /**
     * A handler for the showTotalsPriorChange event.
     */
     showTotalsPriorChange: EventEmitter<string>;

    /**
     * A handler for the stateStoringChange event.
     */
     stateStoringChange: EventEmitter<any>;

    /**
     * A handler for the tabIndexChange event.
     */
     tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the textsChange event.
     */
     textsChange: EventEmitter<any>;

    /**
     * A handler for the useNativeScrollingChange event.
     */
     useNativeScrollingChange: EventEmitter<boolean|
        string>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the wordWrapEnabledChange event.
     */
     wordWrapEnabledChange: EventEmitter<boolean>;





    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'cellClick', emit: 'onCellClick' },
            { subscribe: 'cellPrepared', emit: 'onCellPrepared' },
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'contextMenuPreparing', emit: 'onContextMenuPreparing' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'exported', emit: 'onExported' },
            { subscribe: 'exporting', emit: 'onExporting' },
            { subscribe: 'fileSaving', emit: 'onFileSaving' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { emit: 'allowExpandAllChange' },
            { emit: 'allowFilteringChange' },
            { emit: 'allowSortingChange' },
            { emit: 'allowSortingBySummaryChange' },
            { emit: 'dataFieldAreaChange' },
            { emit: 'dataSourceChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'exportChange' },
            { emit: 'fieldChooserChange' },
            { emit: 'fieldPanelChange' },
            { emit: 'headerFilterChange' },
            { emit: 'heightChange' },
            { emit: 'hideEmptySummaryCellsChange' },
            { emit: 'hintChange' },
            { emit: 'loadPanelChange' },
            { emit: 'rowHeaderLayoutChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'scrollingChange' },
            { emit: 'showBordersChange' },
            { emit: 'showColumnGrandTotalsChange' },
            { emit: 'showColumnTotalsChange' },
            { emit: 'showRowGrandTotalsChange' },
            { emit: 'showRowTotalsChange' },
            { emit: 'showTotalsPriorChange' },
            { emit: 'stateStoringChange' },
            { emit: 'tabIndexChange' },
            { emit: 'textsChange' },
            { emit: 'useNativeScrollingChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' },
            { emit: 'wordWrapEnabledChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxPivotGrid(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-pivot-grid',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'allowExpandAll': [{ type: Input },],
'allowFiltering': [{ type: Input },],
'allowSorting': [{ type: Input },],
'allowSortingBySummary': [{ type: Input },],
'dataFieldArea': [{ type: Input },],
'dataSource': [{ type: Input },],
'disabled': [{ type: Input },],
'elementAttr': [{ type: Input },],
'export': [{ type: Input },],
'fieldChooser': [{ type: Input },],
'fieldPanel': [{ type: Input },],
'headerFilter': [{ type: Input },],
'height': [{ type: Input },],
'hideEmptySummaryCells': [{ type: Input },],
'hint': [{ type: Input },],
'loadPanel': [{ type: Input },],
'rowHeaderLayout': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'scrolling': [{ type: Input },],
'showBorders': [{ type: Input },],
'showColumnGrandTotals': [{ type: Input },],
'showColumnTotals': [{ type: Input },],
'showRowGrandTotals': [{ type: Input },],
'showRowTotals': [{ type: Input },],
'showTotalsPrior': [{ type: Input },],
'stateStoring': [{ type: Input },],
'tabIndex': [{ type: Input },],
'texts': [{ type: Input },],
'useNativeScrolling': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'wordWrapEnabled': [{ type: Input },],
'onCellClick': [{ type: Output },],
'onCellPrepared': [{ type: Output },],
'onContentReady': [{ type: Output },],
'onContextMenuPreparing': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onExported': [{ type: Output },],
'onExporting': [{ type: Output },],
'onFileSaving': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'allowExpandAllChange': [{ type: Output },],
'allowFilteringChange': [{ type: Output },],
'allowSortingChange': [{ type: Output },],
'allowSortingBySummaryChange': [{ type: Output },],
'dataFieldAreaChange': [{ type: Output },],
'dataSourceChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'exportChange': [{ type: Output },],
'fieldChooserChange': [{ type: Output },],
'fieldPanelChange': [{ type: Output },],
'headerFilterChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hideEmptySummaryCellsChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'loadPanelChange': [{ type: Output },],
'rowHeaderLayoutChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'scrollingChange': [{ type: Output },],
'showBordersChange': [{ type: Output },],
'showColumnGrandTotalsChange': [{ type: Output },],
'showColumnTotalsChange': [{ type: Output },],
'showRowGrandTotalsChange': [{ type: Output },],
'showRowTotalsChange': [{ type: Output },],
'showTotalsPriorChange': [{ type: Output },],
'stateStoringChange': [{ type: Output },],
'tabIndexChange': [{ type: Output },],
'textsChange': [{ type: Output },],
'useNativeScrollingChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'wordWrapEnabledChange': [{ type: Output },],
};
}


export class DxPivotGridModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxoDataSourceModule,
    DxiFieldModule,
    DxoFormatModule,
    DxoHeaderFilterModule,
    DxoStoreModule,
    DxoExportModule,
    DxoFieldChooserModule,
    DxoTextsModule,
    DxoFieldPanelModule,
    DxoLoadPanelModule,
    DxoScrollingModule,
    DxoStateStoringModule,
    DxTemplateModule
  ],
  declarations: [
    DxPivotGridComponent
  ],
  exports: [
    DxPivotGridComponent,
    DxoDataSourceModule,
    DxiFieldModule,
    DxoFormatModule,
    DxoHeaderFilterModule,
    DxoStoreModule,
    DxoExportModule,
    DxoFieldChooserModule,
    DxoTextsModule,
    DxoFieldPanelModule,
    DxoLoadPanelModule,
    DxoScrollingModule,
    DxoStateStoringModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
